import React, { useState } from 'react';
import { Search, Plus, Filter, Mail, Phone, ChevronRight } from 'lucide-react';
import { MOCK_LEADS } from '../../mock';
import { Lead, LeadStatus } from '../../types';
import Badge from '../../components/common/Badge';

const statusVariant: Record<LeadStatus, any> = {
  new: 'orange', contacted: 'info', qualified: 'purple',
  proposal: 'warning', won: 'success', lost: 'danger',
};

const AdminLeads: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [selected, setSelected] = useState<Lead | null>(null);

  const filtered = MOCK_LEADS.filter(l => {
    const matchS = statusFilter === 'all' || l.status === statusFilter;
    const matchQ = !search || l.name.toLowerCase().includes(search.toLowerCase()) || l.email.toLowerCase().includes(search.toLowerCase());
    return matchS && matchQ;
  });

  const statusCounts = MOCK_LEADS.reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }), {} as Record<string, number>);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: 'var(--text-primary)', margin: 0 }}>Lead Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{MOCK_LEADS.length} total leads</p>
        </div>
        <button className="btn-primary" style={{ fontSize: '0.875rem' }}>
          <Plus size={16} /> Add Lead
        </button>
      </div>

      {/* Status Pipeline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {(['all', 'new', 'contacted', 'qualified', 'proposal', 'won', 'lost'] as const).map(status => {
          const count = status === 'all' ? MOCK_LEADS.length : (statusCounts[status] || 0);
          const colors: Record<string, { bg: string; text: string }> = {
            all: { bg: 'var(--bg-secondary)', text: 'var(--text-primary)' },
            new: { bg: '#fff7ed', text: '#c2410c' },
            contacted: { bg: '#eff6ff', text: '#1e40af' },
            qualified: { bg: '#f5f3ff', text: '#7e22ce' },
            proposal: { bg: '#fef9c3', text: '#854d0e' },
            won: { bg: '#dcfce7', text: '#166534' },
            lost: { bg: '#fee2e2', text: '#991b1b' },
          };
          const c = colors[status];
          return (
            <button key={status} onClick={() => setStatusFilter(status)} style={{
              padding: '0.75rem', borderRadius: '0.75rem', border: `2px solid ${statusFilter === status ? c.text : 'transparent'}`,
              background: c.bg, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: c.text }}>{count}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: c.text, textTransform: 'capitalize' }}>{status}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <Search size={16} style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input className="input-field" style={{ paddingLeft: '2.5rem' }} placeholder="Search leads by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                {['Lead', 'Service', 'Source', 'Est. Value', 'Assigned', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.875rem 1rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => setSelected(selected?.id === lead.id ? null : lead)}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lead.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: '0.75rem', marginTop: '0.2rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Mail size={10} />{lead.email}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Phone size={10} />{lead.phone}</span>
                    </div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{lead.service.replace('-', ' ')}</td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--text-secondary)' }}>{lead.source}</td>
                  <td style={{ padding: '0.875rem 1rem', fontWeight: 700, color: '#f97316' }}>${lead.estimatedValue.toLocaleString()}</td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{lead.assignedTo ? 'Derek W.' : '—'}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <Badge label={lead.status} variant={statusVariant[lead.status]} />
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="card" style={{ marginTop: '1rem', borderLeft: '4px solid #f97316' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{selected.name}</h3>
              <Badge label={selected.status} variant={statusVariant[selected.status]} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '0.375rem 0.875rem' }}>Convert to Quote</button>
              <button className="btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => setSelected(null)}>✕</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Email', value: selected.email },
              { label: 'Phone', value: selected.phone },
              { label: 'Address', value: selected.address },
              { label: 'Service', value: selected.service.replace(/-/g, ' ') },
              { label: 'Source', value: selected.source },
              { label: 'Est. Value', value: `$${selected.estimatedValue.toLocaleString()}` },
              { label: 'Created', value: selected.createdAt },
            ].map(item => (
              <div key={item.label}>
                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', margin: '0 0 0.2rem' }}>{item.label}</p>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem', margin: 0, textTransform: 'capitalize' }}>{item.value}</p>
              </div>
            ))}
          </div>
          {selected.notes && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.625rem' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 0.25rem' }}>Notes</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>{selected.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
